import { formatHumanName, MedplumClient } from '@medplum/core';
import { HumanName, Organization, OrganizationContact } from '@medplum/fhirtypes';
import { IconMail, IconPhone, IconWorld, IconPrinter } from '@tabler/icons-react';
import React from 'react';

export function toHumanName(name: string): HumanName {
  const parts = name.split(' ');
  const family = parts.pop() || '';
  const given = [parts.join(' ')];
  return {
    use: 'official',
    family,
    given,
  };
}

export type TelecomSystem = 'phone' | 'email' | 'url';

export function inferSystem(value: string): TelecomSystem | 'other' {
  const v = value.trim();
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
  const urlRe = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[\w#?=&.-]*)?$/i;
  const phoneRe = /^(\+?[0-9\s().-]{7,})$/; // basic phone-like pattern
  if (emailRe.test(v)) return 'email';
  if (urlRe.test(v)) return 'url';
  if (phoneRe.test(v)) return 'phone';
  return 'other';
}

export function getSystemIcon(system: string, fallback: React.ExoticComponent) {
  if (system === 'phone') return IconPhone;
  if (system === 'email') return IconMail;
  if (system === 'url') return IconWorld;
  if (system === 'fax') return IconPrinter;
  return fallback;
}

export function setContactTelecomAt(contact: OrganizationContact, index: number, value: string): OrganizationContact {
  const telecom = contact.telecom ?? [];
  const next = [...telecom];
  next[index] = { ...(next[index] as any), value } as any;
  return { ...contact, telecom: next };
}

export function addContactTelecomEntry(contact: OrganizationContact, rawValue: string): OrganizationContact {
  const v = rawValue.trim();
  if (!v) return contact;
  const telecom = contact.telecom ?? [];
  const system = inferSystem(v) ?? 'phone';
  const next = [...telecom, { id: new Date().toISOString(), system, value: v } as any];
  return { ...contact, telecom: next };
}

export function removeContactTelecomAt(contact: OrganizationContact, index: number): OrganizationContact {
  const telecom = contact.telecom ?? [];
  const next = [...telecom];
  next.splice(index, 1);
  return { ...contact, telecom: next };
}

export function removeOrgTelecomAt(organization: Organization, index: number): Organization {
  const next = [...(organization.telecom || [])];
  next.splice(index, 1);
  return {
    ...organization,
    telecom: next,
    address: organization.address,
  };
}

export function setOrgTelecomAt(organization: Organization, index: number, value: string): Organization {
  const next = [...(organization.telecom || [])];
  next[index] = { ...next[index], value } as any;
  return {
    ...organization,
    telecom: next,
    address: organization.address,
  };
}

export function setOrgAddressAt(organization: Organization, index: number, value: string): Organization {
  const next = [...(organization.address || [])];
  next[index] = { ...next[index], text: value } as any;
  return {
    ...organization,
    address: next,
  };
}

export function removeOrgAddressAt(organization: Organization, index: number): Organization {
  const next = [...(organization.address || [])];
  next.splice(index, 1);
  return {
    ...organization,
    address: next,
  };
}

export function getAvatarInitials(name?: HumanName): string {
  return !name
    ? ''
    : formatHumanName(name)
        .split(' ')
        .filter(Boolean)
        .map((part) => part[0]?.toUpperCase())
        .slice(0, 2) // limit to 2 letters max
        .join('');
}

export async function createOrganizationOrGetExisting(
  medplum: MedplumClient,
  organizationName: string,
  faxNumber: string
): Promise<Organization> {
  const organizationsWithSameName = await medplum.searchResources('Organization', {
    'name:exact': organizationName,
  });

  const noOrganizationWithSameName =
    !organizationsWithSameName.bundle.entry ||
    (organizationsWithSameName.bundle.entry.length && organizationsWithSameName.bundle.entry.length === 0);
  if (noOrganizationWithSameName) {
    return await medplum.createResource({
      resourceType: 'Organization',
      name: organizationName,
      contact: [
        {
          name: { given: ['Fax'] },
          telecom: [{ system: 'fax', value: faxNumber }],
        },
      ],
    });
  }

  const organization = organizationsWithSameName.bundle.entry?.[0]?.resource as Organization;

  const isFaxContactAlreadyPresent = organization.contact?.find((contact) =>
    contact.telecom?.some((telecom) => telecom.system === 'fax' && telecom.value === faxNumber)
  );
  if (isFaxContactAlreadyPresent) {
    return organization;
  }

  const updatedOrganization = {
    ...organization,
    contact: [
      ...(organization.contact || []),
      {
        name: { given: ['Fax'] },
        telecom: [{ system: 'fax', value: faxNumber }],
      } as OrganizationContact,
    ],
  };

  return medplum.updateResource(updatedOrganization);
}
